/**
 * The `Path` view for the multiple-propagation-constraint inspector.
 *
 * Lives in its own file (a default-exported component) so the inspector module
 * exports only its default component: a named `export const Path = observer(...)`
 * sitting alongside the default export is a non-component value export to the
 * Fast Refresh boundary check, which forces a full reload on edits.
 *
 * Classic JSX runtime (app-wide esbuild transform) needs React in scope.
 */
import React from 'react';
import { observer } from 'mobx-react';
import type { IPathElement, DisplayPath } from './mpcModels';

/** Props for the {@link Path} component. */
interface PathProps {
  /** A list of elements in the path (optional when `path` is given). */
  elements?: IPathElement[];
  /** The path (optional when `elements` is given). */
  path?: DisplayPath;
  /**
   * Whether this path is rendered inside a react-select option list. The old
   * `div:not(.Select) .path-visualization` rule added `padding-left: 10px`
   * everywhere EXCEPT inside a `.Select`; replace that global guard with this
   * call-site flag (the only no-pad caller is the `PathOption` renderer).
   */
  inSelect?: boolean;
}

/**
 * Component to render a Path
 *
 * Props:
 *  - elements: a list of elements in the path (optional when path is given)
 *  - path: the path (optional when elements is given)
 *  - inSelect: rendered inside a react-select option list (drops the left pad)
 */
const Path = observer(function Path({ elements: elementList, path, inSelect = false }: PathProps) {
  const firstObjectName =
    elementList != null && elementList.length > 0
      ? elementList[0].previousObject.name
      : path!.firstObject!.name;
  // Both `elementList` and `path.elements` carry the real Dependency/Inheritance
  // edges at runtime; `IPath` declares its `elements` as the structural `{ id }`
  // shape, so view the chosen list as `IPathElement[]` for the display reads.
  const elements = (
    elementList != null && elementList.length > 0 ? elementList : path!.elements
  ) as IPathElement[];
  return (
    <div className={inSelect ? 'path-visualization' : 'path-visualization pl-2.5'}>
      <div className="object-type">{firstObjectName}</div>
      {elements.map((e) => (
        <React.Fragment key={e.id}>
          <div className="element">{e.displayName}</div>
          <div className="object-type">{e.nextObject.name}</div>
        </React.Fragment>
      ))}
    </div>
  );
});

export default Path;
